/**
 * The tuning A/B swap — hoisted out of mount() so the rule is TESTABLE (it used to be a closure
 * inside the composition root, i.e. untestable by construction, which is how it shipped broken).
 *
 * What it is: a dev affordance that swaps the live `tuning` object between two slots without
 * resetting the player's position — the only honest way to judge a 15 ms feel change. Slot B
 * starts as the `ue-parity` preset so "what we had vs what we're building" is one keypress.
 *
 * TWO FENCES, because this handler mutates the movement model with no on-screen tell:
 *
 *  1. A MODIFIER CHORD. The binding is **Alt+P** ("P for preset"). It used to be bare `Tab`, and
 *     `Tab` is the key a player presses to navigate a card full of buttons — one stray Tab inside
 *     an encounter silently dropped maxRunSpeed 7.0 → 4.3 and groundAccel 48 → 20, and because
 *     the handler called preventDefault() the focus did not even move, so there was no tell at
 *     all. That is the owner's "the game gets worse after every artifact" (perf diagnosis §2).
 *     Alt+P is not a focus-navigation key, so a stray press cannot reach it.
 *  2. INPUT OWNERSHIP. `handle()` returns false without touching anything — and without asking
 *     the caller to preventDefault — whenever the UI owns the keyboard. mount wires
 *     `uiHasFocus` in; the predicate itself lives with the layer registry (see
 *     src/input/ownership.ts).
 *
 * Belt and braces on purpose: either fence alone closes the bug, and neither is expensive.
 */
import type { MovementTuning } from '../sim/tuning';

/** The chord, in one place — README and CLAUDE.md quote it; tests assert against it. */
export const PRESET_SWAP_CHORD = { code: 'KeyP', alt: true } as const;

/** Human-readable binding for docs/logs. */
export const PRESET_SWAP_LABEL = 'Alt+P';

/** Is this event the swap chord? Plain modifier check — Alt only, never Ctrl/Meta. */
export function isPresetSwapChord(e: KeyboardEvent): boolean {
  return e.code === PRESET_SWAP_CHORD.code && e.altKey && !e.ctrlKey && !e.metaKey;
}

export interface PresetSwapOptions {
  /** The LIVE tuning object the sim reads — mutated in place (Object.assign), never replaced. */
  tuning: MovementTuning;
  /** Slot B's starting contents (mount passes the `ue-parity` preset). */
  slotB: MovementTuning;
  /** Does the UI own the keyboard? Injected so this module stays DOM-free and testable. */
  uiHasFocus(): boolean;
  /** Called after a swap lands: recompute derived values, refresh the panel, log the slot. */
  onSwapped(slot: 'A' | 'B'): void;
}

export interface PresetSwap {
  /**
   * Feed a keydown. Returns true ONLY when the swap actually happened — the caller uses that
   * return to decide whether to preventDefault(), so a refused swap never eats the key.
   */
  handle(e: KeyboardEvent): boolean;
  /** Which slot is live (diagnostics/tests). */
  slot(): 'A' | 'B';
}

export function createPresetSwap(opts: PresetSwapOptions): PresetSwap {
  const slots: Record<'A' | 'B', MovementTuning> = {
    A: { ...opts.tuning },
    B: { ...opts.slotB },
  };
  let active: 'A' | 'B' = 'A';

  return {
    handle(e) {
      if (!isPresetSwapChord(e)) return false; // fence 1: the chord
      if (opts.uiHasFocus()) return false; // fence 2: the UI owns the keyboard
      slots[active] = { ...opts.tuning }; // park any panel edits back into the live slot
      active = active === 'A' ? 'B' : 'A';
      Object.assign(opts.tuning, slots[active]);
      opts.onSwapped(active);
      return true;
    },
    slot: () => active,
  };
}
